import React, { ChangeEvent, useState, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  styled,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { MobileWidth, TextSizes } from "entities/constants";
import { TextSizeContext } from "data/index";
import useScreenSize from "hooks/ScreenSize";

import TextBox from "ui/components/shared/TextBox";
import { SubmitButton } from "ui/components/shared/SubmitButton";
import ConfirmationModal from "ui/components/shared/ConfirmationModal";
import { ChangePasswordInterface, SecurityTabPropType } from "entities/interfaces";
import WithSecurityTabData from "ui/containers/settings/SecurityTabContainer";

// Styled Components
const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "2.5rem",
  padding: "2rem",
  [theme.breakpoints.down("sm")]: {
    padding: "1.5rem 1rem",
  },
}));

const Card = styled(Paper)(({ theme }) => ({
  padding: "2rem",
  borderRadius: "20px",
  boxShadow: theme.shadows[2],
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.25rem",
  color: theme.palette.text.primary,
}));

const SectionDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.text.secondary,
}));

const SecurityTab = ({ updatePassword, deleteAccount }: SecurityTabPropType) => {
  const { t } = useTranslation();
  const { state: textSize } = useContext(TextSizeContext);
  const { width } = useScreenSize();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ChangePasswordInterface>({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [error, setError] = useState<ChangePasswordInterface>({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [saveDisabled, setSaveDisabled] = useState(true);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);

  const handleFormChange = (
    event: ChangeEvent<HTMLInputElement>,
    key: keyof ChangePasswordInterface
  ) => {
    setFormData({ ...formData, [key]: event.target.value });
    setError({ ...error, [key]: "" });
    setSaveDisabled(false);
  };

  const handleSave = async () => {
    const { oldPassword, newPassword, confirmPassword } = formData;

    if (!oldPassword || !newPassword) {
      setError({
        ...error,
        oldPassword: !oldPassword ? `${t("Current Password")} ${t("required")}` : "",
        newPassword: !newPassword ? `${t("New Password")} ${t("required")}` : "",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setError({
        ...error,
        confirmPassword: t("Password didn't match!"),
      });
      return;
    }

    const resp = await updatePassword(formData);
    if (resp.status === 200) {
      setSaveDisabled(true);
    }
  };

  const handleDelete = async () => {
    const resp = await deleteAccount();
    if (resp.status === 200) {
      localStorage.removeItem("currentUserName");
      localStorage.removeItem("currentUserToken");
      navigate("/auth");
    }
  };

  return (
    <Container>
      {/* Passwort ändern */}
      <Card>
        <SectionTitle>{t("Change Password")}</SectionTitle>
        <SectionDescription>
          {t("Settings and recommendations to help you keep your account secure")}
        </SectionDescription>

        <Divider />

        <TextBox
          label={t("Current Password")}
          value={formData.oldPassword}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleFormChange(e, "oldPassword")}
          type="password"
          error={error.oldPassword}
          isRequired
        />
        <TextBox
          label={t("New Password")}
          value={formData.newPassword}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleFormChange(e, "newPassword")}
          type="password"
          error={error.newPassword}
          isRequired
        />
        <TextBox
          label={t("Confirm Password")}
          value={formData.confirmPassword}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleFormChange(e, "confirmPassword")}
          type="password"
          error={error.confirmPassword}
          isRequired
        />

        <SubmitButton
          color="success"
          onClick={handleSave}
          disabled={saveDisabled}
        >
          {t("Save")}
        </SubmitButton>
      </Card>

      {/* Konto löschen */}
      <Card>
        <SectionTitle>{t("Delete Account")}</SectionTitle>
        <SectionDescription>
          {t("Deleting your account will remove all of your information from out database. This cannot be undone.")}
        </SectionDescription>

        <SubmitButton
          color="error"
          onClick={() => setDeleteConfirmationModal(true)}
        >
          {t("Delete Account")}
        </SubmitButton>
      </Card>

      {deleteConfirmationModal && (
        <ConfirmationModal
          closeModal={() => setDeleteConfirmationModal(false)}
          confirmColor="error"
          handleConfirm={handleDelete}
          title={t("Deleting Account")}
          description={t("Are you sure you want to delete this account?")}
        />
      )}
    </Container>
  );
};

export default WithSecurityTabData(SecurityTab);
